import { Controller, useFormContext } from "react-hook-form";
import { Form, Icon, Popup } from "semantic-ui-react";

import "./PricingTier.scss";
import bulkPricingTierSchema from "shared/schemas/products/bulkPricingTierSchema";
import PriceField from "./PriceField";
import { LabeledInput } from "dyl-components";

const PricingTier = ({
    fieldName,
    index,
    addRange,
    updateRange,
    currentTiers,
    removeRange,
}) => {
    const { control, trigger, watch } = useFormContext();
    const maxDiscount = watch("max_discount");
    const schedule = watch("price_data.schedule");
    const isDiscountDisabled = !maxDiscount || Number(maxDiscount) <= 0;
    const isRecurring = schedule === "recurring";

    return (
        <Form.Group>
            <Controller
                name={`${fieldName}[${index}].start`}
                control={control}
                render={({
                    field: { name, value: start, onChange },
                    fieldState: { error },
                }) => (
                    <>
                        <Form.Input
                            label="Quantity"
                            width={isRecurring ? 3 : 4}
                            required
                            value={start}
                            name={name}
                            onChange={(_, { name, value }) => {
                                onChange({ target: { name, value } });
                            }}
                            error={error?.message}
                            readOnly={index > 0}
                        />
                        <Form.Field
                            control="div"
                            className="PricingTier__field--hidden-label"
                            label="Quantity"
                            width={1}
                        >
                            <Icon
                                style={{ marginTop: "0.75em" }}
                                name="minus"
                            />
                        </Form.Field>
                        <Controller
                            name={`${fieldName}[${index}].end`}
                            control={control}
                            render={({
                                field: { name, value, onChange },
                                fieldState: { error },
                            }) => (
                                <Form.Input
                                    className="PricingTier__field--hidden-label"
                                    label="Quantity"
                                    width={isRecurring ? 3 : 4}
                                    value={value}
                                    name={name}
                                    onChange={async (_, { name, value }) => {
                                        onChange({ target: { name, value } });
                                        const isValid =
                                            await bulkPricingTierSchema.end.isValid(
                                                value
                                            );
                                        if (!Number.isNaN(value) && start < Number(value) && isValid) {
                                            if (!currentTiers[index + 1]) {
                                                addRange({
                                                    start: Number(value) + 1,
                                                    end: null,
                                                    max_discount_cycle: 1,
                                                    life_time_discount: false,
                                                });
                                            } else {
                                                updateRange(index + 1, {
                                                    ...currentTiers[index + 1],
                                                    start: Number(value) + 1,
                                                });
                                                trigger(`${fieldName}[${index + 1}].end`);
                                            }
                                        }
                                    }}
                                    error={error?.message}
                                />
                            )}
                        />
                    </>
                )}
            />
            <PriceField 
                label={"Unit Price"}
                name={`${fieldName}[${index}].price`}
                width={isRecurring ? 4 : 6}
            />

            {isRecurring && (
                <Form.Field width={5}>
                    <Controller
                        control={control}
                        name={`${fieldName}[${index}].max_discount_cycle`}
                        defaultValue={1}
                        render={({ field: { value, onChange } }) => (
                            <LabeledInput
                                label="Discount Expiration"
                                prefix="Billing Cycle"
                                type="number"
                                value={value}
                                inputProps={{
                                    min: 1
                                }}
                                onChange={onChange}
                                required
                                disabled={isDiscountDisabled}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name={`${fieldName}[${index}].life_time_discount`}

                        render={({ field: { value, onChange } }) => (
                            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "10px", opacity: isDiscountDisabled ? 0.5 : 1 }}>
                                <Form.Checkbox
                                    label="No Expiration"
                                    checked={!!value}
                                    disabled={isDiscountDisabled}
                                    onChange={(_, { checked }) => onChange(checked)}
                                />
                                <Popup
                                    content={"Lifetime discount"}
                                    inverted
                                    position="bottom right"
                                    trigger={<Icon className="fas fa-info-circle" color="primary" />}
                                    disabled={isDiscountDisabled}
                                />
                            </div>
                        )}
                    />
                </Form.Field>
            )}

            <Form.Field
                control="div"
                className="PricingTier__field--hidden-label"
                label="Remove"
                width={1}
            >
                {index > 0 && index === currentTiers.length - 1 && (
                    <Icon
                        style={{ marginTop: "0.75em" }}
                        className="fas fa-times"
                        link
                        onClick={() => {
                            updateRange(index - 1, {
                                ...currentTiers[index - 1],
                                end: null,
                            });
                            removeRange(index);
                        }}
                    />
                )}
            </Form.Field>
        </Form.Group>
    );
};

export default PricingTier;
